import { LocatableType } from "./orders";

export enum SubscriptionStatus {
  Active = "active",
  InComplete = "incomplete",
  PendingCancel = "pending_cancel",
  Canceled = "canceled",
}

interface Locatable {
  id: string;
  name: string;
  shortAddress: string;
}

function toLocatable(data: any) {
  if (!data) return undefined;

  return {
    id: data.id,
    name: data.name,
    shortAddress: data.short_address,
  };
}

export interface Subscription {
  id: string;
  name: string;
  email: string;
  status: string;
  startDate: string;
  cancelReason: string;
  membershipName: string;
  membershipPlanName: string;
  locatable?: Locatable;
  locatableType: LocatableType;
  orderConfirmation: string;
  orderID: string;
  endDate?: string;
  cancellationDate?: string;
  cancelledAt?: string;
  membershipPlanPortal: boolean;
  paymentStatus: "paid" | "payment_failed";
  lastTransactionDate: string;
}

export const toSubscription = (data: any): Subscription => {
  return {
    id: data.id,
    name: data.member_name,
    email: data.email,
    status: data.status,
    startDate: data.start_date,
    cancelReason: data.cancel_reason,
    membershipName: data.membership_name,
    membershipPlanName: data.membership_plan_name,
    locatableType: data.locatable_type,
    locatable: toLocatable(data.locatable),
    orderConfirmation: data.order_confirmation,
    orderID: data.order_id,
    endDate: data.end_date,
    cancellationDate: data.cancellation_date,
    paymentStatus: data.payment_status,
    cancelledAt: data.cancelled_at,
    membershipPlanPortal: data.membership_plan_portal,
    lastTransactionDate: data.last_transaction_date,
  };
};

export enum MembershipProductType {
  HotDeskMembership = "hot_desk_membership",
  DedicatedDeskMembership = "dedicated_desk_membership",
  AllAccessMembership = "all_access_membership",
  PrivateOfficeMembership = "private_office_membership",
  VirtualMembership = "virtual_membership",
}

export interface SubscriptionByMembershipType {
  type: string;
  count: number;
}

export interface MembershipReport {
  totalRevenue: number;
  currency: string;
  totalNewSubscriptions: number;
  subscriptionsByMembershipType: SubscriptionByMembershipType[];
}

export function toMembershipReport(data: any): MembershipReport {
  return {
    totalRevenue: data.total_revenue,
    currency: data.currency,
    totalNewSubscriptions: data.total_new_subscriptions,
    subscriptionsByMembershipType: toSubscriptionByMembershipType(
      data.subscriptions_group_by_product_type
    ),
  };
}

function toSubscriptionByMembershipType(
  data: any
): SubscriptionByMembershipType[] {
  let result: SubscriptionByMembershipType[] = [];
  if (!!data[MembershipProductType.AllAccessMembership]) {
    result.push({
      type: MembershipProductType.AllAccessMembership,
      count: data[MembershipProductType.AllAccessMembership],
    });
  }
  if (!!data[MembershipProductType.HotDeskMembership]) {
    result.push({
      type: MembershipProductType.HotDeskMembership,
      count: data[MembershipProductType.HotDeskMembership],
    });
  }
  if (!!data[MembershipProductType.DedicatedDeskMembership]) {
    result.push({
      type: MembershipProductType.DedicatedDeskMembership,
      count: data[MembershipProductType.DedicatedDeskMembership],
    });
  }
  if (!!data[MembershipProductType.PrivateOfficeMembership]) {
    result.push({
      type: MembershipProductType.PrivateOfficeMembership,
      count: data[MembershipProductType.PrivateOfficeMembership],
    });
  }
  if (!!data[MembershipProductType.VirtualMembership]) {
    result.push({
      type: MembershipProductType.VirtualMembership,
      count: data[MembershipProductType.VirtualMembership],
    });
  }
  return result;
}

export interface DownloadMembershipsParam {
  startDate?: string;
  endDate?: string;
  dataType?: string;
}

export interface SubscriptionTransaction {
  id: string;
  totalPrice: number;
  currency: string;
  transactionType: "charge" | "refund" | "failed";
  cardBrand: string;
  cardEnding: string;
  createdAt: string;
  cardNetworkUrl: string;
}

export const toSubscriptionTransaction = (
  data: any
): SubscriptionTransaction => {
  return {
    id: data.id,
    totalPrice: data.total_price,
    currency: data.currency,
    transactionType: data.transaction_type,
    cardBrand: data.card_brand,
    cardEnding: data.card_ending,
    createdAt: data.created_at,
    cardNetworkUrl: data.card_network_url,
  };
};
